import React, { useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import {
  useBulkUploadModal,
  type IncompleteBulkUploadEnrolledThrough,
  type UploadType,
} from "./useBulkUploadModal";
import { ENROLMENT_TEMPLATE_URLS } from "../../../api/academyEnrolments/enrolmentsService";
import "./BulkUploadModal.css";

interface BulkUploadModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess?: () => void;
}

const UPLOAD_TYPE_CONFIG: Record<
  UploadType,
  { label: string; description: string; icon: string; color: string; maxRecords: number }
> = {
  dynamic: {
    label: "Dynamic Bulk Upload",
    description: "Upload enrolments with dynamic (flexible) slot assignments.",
    icon: "ri-exchange-line",
    color: "#0d6efd",
    maxRecords: 30,
  },
  fixed: {
    label: "Fixed Bulk Upload",
    description: "Upload enrolments with fixed slot assignments.",
    icon: "ri-price-tag-3-line",
    color: "#198754",
    maxRecords: 30,
  },
  incomplete: {
    label: "Incomplete Bulk Upload",
    description: "Complete missing entries for existing enrolment records.",
    icon: "ri-file-warning-line",
    color: "#fd7e14",
    maxRecords: 50,
  },
};

const INCOMPLETE_ENROLLED_THROUGH_CONFIG: Record<
  IncompleteBulkUploadEnrolledThrough,
  { label: string; description: string; icon: string; color: string }
> = {
  Offline: {
    label: "Offline",
    description: "Complete missing entries for offline enrolment records.",
    icon: "ri-store-2-line",
    color: "#198754",
  },
  Receivables: {
    label: "Receivables",
    description: "Complete missing entries for receivables enrolment records.",
    icon: "ri-bank-card-line",
    color: "#0d6efd",
  },
};

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({ show, onHide, onSuccess }) => {
  const { functions, values, loading } = useBulkUploadModal({ show, onHide, onSuccess });
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const handleDropZoneClick = () => {
    values.fileInputRef.current?.click();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0] ?? null;
    functions.handleFileChange(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const currentTypeConfig = values.uploadType ? UPLOAD_TYPE_CONFIG[values.uploadType] : null;
  const templateAvailable =
    values.uploadType ? !!ENROLMENT_TEMPLATE_URLS[values.uploadType] : false;

  const modalTitle = () => {
    if (values.step === "type-select") return "Bulk Upload Enrolments";
    if (values.step === "incomplete-source-select") return "Incomplete Bulk Upload";
    if (values.step === "upload" && currentTypeConfig) return currentTypeConfig.label;
    return "Upload Results";
  };

  return (
    <Modal
      show={show}
      onHide={functions.handleClose}
      centered
      size="lg"
      className="bulk-upload-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{modalTitle()}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* ── Step 1: Type selection ── */}
        {values.step === "type-select" && (
          <div>
            <p className="text-muted mb-4" style={{ fontSize: 14 }}>
              Select the type of bulk upload you want to perform. Each type uses a different
              template and upload process.
            </p>
            <div className="bulk-upload-type-cards">
              {(Object.entries(UPLOAD_TYPE_CONFIG) as [UploadType, typeof UPLOAD_TYPE_CONFIG[UploadType]][]).map(
                ([type, config]) => (
                  <button
                    key={type}
                    type="button"
                    className="bulk-upload-type-card"
                    onClick={() => functions.handleSelectType(type)}
                    style={{ "--card-color": config.color } as React.CSSProperties}
                  >
                    <i className={`${config.icon} bulk-upload-type-card-icon`} />
                    <div className="bulk-upload-type-card-label">{config.label}</div>
                    <div className="bulk-upload-type-card-desc">{config.description}</div>
                    <div className="bulk-upload-type-card-limit">
                      Max {config.maxRecords} records / upload
                    </div>
                  </button>
                )
              )}
            </div>
          </div>
        )}

        {/* ── Step 2: Incomplete source selection ── */}
        {values.step === "incomplete-source-select" && (
          <div>
            <p className="text-muted mb-4" style={{ fontSize: 14 }}>
              Select which enrolment source this incomplete upload should update.
            </p>
            <div className="bulk-upload-source-cards">
              {(
                Object.entries(INCOMPLETE_ENROLLED_THROUGH_CONFIG) as [
                  IncompleteBulkUploadEnrolledThrough,
                  typeof INCOMPLETE_ENROLLED_THROUGH_CONFIG[IncompleteBulkUploadEnrolledThrough]
                ][]
              ).map(([enrolledThrough, config]) => (
                <button
                  key={enrolledThrough}
                  type="button"
                  className="bulk-upload-type-card"
                  onClick={() => functions.handleSelectIncompleteEnrolledThrough(enrolledThrough)}
                  style={{ "--card-color": config.color } as React.CSSProperties}
                >
                  <i className={`${config.icon} bulk-upload-type-card-icon`} />
                  <div className="bulk-upload-type-card-label">{config.label}</div>
                  <div className="bulk-upload-type-card-desc">{config.description}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 3: File upload ── */}
        {values.step === "upload" && (
          <>
            {/* Hidden file input */}
            <input
              ref={values.fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={(e) => functions.handleFileChange(e.target.files?.[0] ?? null)}
            />

            {/* Drop zone */}
            <div
              ref={dropZoneRef}
              className={`bulk-upload-drop-zone${values.selectedFile ? " has-file" : ""}${loading.isUploading ? " is-uploading" : ""}`}
              onClick={!loading.isUploading ? handleDropZoneClick : undefined}
              onDrop={!loading.isUploading ? handleDrop : undefined}
              onDragOver={!loading.isUploading ? handleDragOver : undefined}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                !loading.isUploading && e.key === "Enter" && handleDropZoneClick()
              }
            >
              {loading.isUploading ? (
                <>
                  <i className="ri-loader-4-line bulk-upload-spinner" />
                  <div className="drop-title">Uploading…</div>
                  <div className="drop-subtitle">Please wait while we process your file.</div>
                  {/* Progress bar */}
                  <div className="bulk-upload-progress">
                    <div
                      className="bulk-upload-progress-bar"
                      style={{ width: `${values.uploadProgress}%` }}
                    />
                  </div>
                  <div className="bulk-upload-progress-label">{values.uploadProgress}%</div>
                </>
              ) : (
                <>
                  <i
                    className={values.selectedFile ? "ri-file-excel-line" : "ri-upload-cloud-line"}
                  />
                  {values.selectedFile ? (
                    <>
                      <div className="drop-title">File selected</div>
                      <div className="file-name">{values.selectedFile.name}</div>
                      <div className="drop-subtitle" style={{ marginTop: 4 }}>
                        Click to change file
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="drop-title">Click to select or drag & drop file here</div>
                      <div className="drop-subtitle">Supports .xlsx and .xls</div>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Error */}
            {values.uploadError && (
              <div className="text-danger small mt-2">{values.uploadError}</div>
            )}

            {/* Instructions */}
            <div className="bulk-upload-instructions">
              <strong>Instructions:</strong>
              <ul className="mt-1 mb-0 ps-3">
                <li>Download the template below and fill it with the required data.</li>
                <li>Only .xlsx and .xls files are accepted.</li>
                <li>
                  Upload type:{" "}
                  <strong style={{ color: currentTypeConfig?.color }}>
                    {currentTypeConfig?.label}
                  </strong>
                </li>
                {values.uploadType === "incomplete" && (
                  <li>
                    Enrolment source: <strong>{values.incompleteEnrolledThrough}</strong>
                  </li>
                )}
                <li>
                  Maximum{" "}
                  <strong>{currentTypeConfig?.maxRecords} records</strong> can be uploaded
                  at a time.
                </li>
                <li>Invalid rows will be listed after upload — valid rows are saved.</li>
              </ul>
            </div>
          </>
        )}

        {/* ── Step 4: Result ── */}
        {values.step === "result" && values.uploadResult && (
          <>
            {values.uploadResult.isValidationError ? (
              /* ── Validation error view ── */
              <>
                <div className="bulk-upload-validation-error-header">
                  <i className="ri-error-warning-line" />
                  <div>
                    <div className="validation-error-title">Validation Failed</div>
                    <div className="validation-error-subtitle">
                      {values.uploadResult.validationMessage}
                    </div>
                  </div>
                </div>
                <div className="bulk-upload-validation-errors-list">
                  {values.uploadResult.validationErrors.map((err, idx) => (
                    <div key={idx} className="bulk-upload-validation-error-item">
                      <i className="ri-close-circle-line" />
                      <span>{err}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              /* ── Success / records view ── */
              <>
                {/* Summary cards */}
                <div className="bulk-upload-result-summary">
                  <div className="bulk-upload-result-card success">
                    <div className="result-count">{values.uploadResult.successCount}</div>
                    <div className="result-label">Successful</div>
                  </div>
                  <div className="bulk-upload-result-card skipped">
                    <div className="result-count">{values.uploadResult.skippedCount}</div>
                    <div className="result-label">Skipped</div>
                  </div>
                  <div className="bulk-upload-result-card failed">
                    <div className="result-count">{values.uploadResult.failedCount}</div>
                    <div className="result-label">Failed</div>
                  </div>
                </div>

                {/* API message */}
                {values.uploadResult.message && (
                  <div className="bulk-upload-result-message">{values.uploadResult.message}</div>
                )}

                {/* Per-player records */}
                {values.uploadResult.records.length > 0 && (
                  <div className="bulk-upload-records-list">
                    {values.uploadResult.records.map((rec, idx) => (
                      <div key={idx} className={`bulk-upload-record-item status-${rec.status.toLowerCase()}`}>
                        <div className="record-main">
                          <span className="record-player">{rec.player}</span>
                          {rec.reason && (
                            <span className="record-reason">{rec.reason}</span>
                          )}
                        </div>
                        <span className={`record-status-badge badge-${rec.status.toLowerCase()}`}>
                          {rec.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {values.step === "type-select" && (
          <Button variant="outline-dark" onClick={functions.handleClose}>
            Cancel
          </Button>
        )}

        {values.step === "incomplete-source-select" && (
          <>
            <Button variant="outline-dark" onClick={functions.handleBackToTypeSelect}>
              Back
            </Button>
            <Button variant="outline-dark" onClick={functions.handleClose}>
              Cancel
            </Button>
          </>
        )}

        {values.step === "upload" && (
          <>
            {templateAvailable && (
              <Button
                variant="link"
                className="bulk-upload-template-btn me-auto p-0"
                onClick={functions.handleDownloadTemplate}
                disabled={loading.isUploading}
              >
                Download Template
              </Button>
            )}
            {!templateAvailable && (
              <span className="me-auto text-muted small" style={{ alignSelf: "center" }}>
                Template coming soon
              </span>
            )}
            <Button
              variant="outline-dark"
              onClick={functions.handleBackFromUpload}
              disabled={loading.isUploading}
            >
              Back
            </Button>
            <Button
              variant="primary"
              onClick={functions.handleUpload}
              disabled={loading.isUploading || !values.selectedFile}
            >
              {loading.isUploading ? "Uploading…" : "Upload"}
            </Button>
          </>
        )}

        {values.step === "result" && (
          <>
            <Button variant="outline-secondary" onClick={functions.handleReset}>
              Upload Another File
            </Button>
            <Button variant="primary" onClick={functions.handleClose}>
              Done
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkUploadModal;
